import Batch from 'smg/batch';
import { GetLineItems } from 'smg/customer-api/lineitems';
import { OpenQuotes } from 'smg/customer-api/v2/quotes';
import * as models from 'smg/customer-api/models';
import { Loader, MultiLoader } from 'smg/multiloader';
import { GetProofURLs } from 'smg/proofs';
import { Session } from 'smg/customer-api/session';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as lineitems from '../lineitems';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

main();

function main() {
    const $form = document.getElementById('quote-search-form') as HTMLFormElement;
    const $input = document.getElementById('quote-search-input') as HTMLInputElement;
    const $empty = document.getElementById('state-empty') as HTMLElement;
    const $results = document.getElementById('state-results') as HTMLElement;
    const $resultsList = document.getElementById('results-list') as HTMLElement;
    const $resultsSummary = document.getElementById('results-summary') as HTMLElement;
    const $noResults = document.getElementById('state-no-results') as HTMLElement;
    const $noResultsMessage = document.getElementById('no-results-message') as HTMLElement;
    const $clearSearch = document.getElementById('clear-search') as HTMLAnchorElement;
    const $loadMore = document.getElementById('quote-search-load-more') as HTMLButtonElement;
    const $loadMoreSpinner = $loadMore.querySelector('.spinner-border') as HTMLElement;

    let ml: MultiLoader | undefined;
    let lastPage = false;

    function showState(state: 'empty' | 'results' | 'no-results') {
        $empty.classList.toggle('d-none', state !== 'empty');
        $results.classList.toggle('d-none', state !== 'results');
        $noResults.classList.toggle('d-none', state !== 'no-results');
    }

    function search(q: string) {
        $resultsList.innerHTML = '';
        $resultsList.appendChild(template.Clone('#search-skeleton-template'));
        $resultsSummary.textContent = `Showing quotes for "${q}"`;
        $loadMore.classList.add('d-none');
        showState('results');
        QueryString().set('q', q);

        lastPage = false;

        const currentML = new MultiLoader({
            $target: $resultsList,
            pageSize: 10,
            loaders: [
                new Loader<models.Quote>({
                    name: 'quotes',
                    load: (b: Batch, page: number, pageSize: number) => {
                        const quotes = OpenQuotes({
                            sort: 'created asc',
                            max: pageSize,
                            p: page,
                            data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteRequest,key',
                            q,
                        }).then(r => {
                            lastPage = !r.nextPage;
                            return { rows: r.results, done: !r.nextPage };
                        });
                        void GetLineItems();
                        return quotes;
                    },
                    element: quote => renderQuoteCard(quote),
                }),
            ],
            // match the server sort: created asc
            sort: (a, b) => {
                if (!(a instanceof models.Quote) || !(b instanceof models.Quote)) {
                    return 0;
                }
                return (a.created?.toMillis() ?? 0) - (b.created?.toMillis() ?? 0);
            },
            done: () => {
                lastPage = true;
                $loadMore.classList.add('d-none');
                if (!$resultsList.children.length) {
                    $noResultsMessage.textContent =
                        `Nothing came up for "${q}". Double-check your email, phone, or quote number and try again.`;
                    showState('no-results');
                }
            },
        });
        ml = currentML;

        void currentML.load(true).then(() => {
            if (ml !== currentML || !$resultsList.children.length) {
                return;
            }
            $loadMore.classList.toggle('d-none', lastPage);
        }).catch(() => {
            if (ml !== currentML) {
                return;
            }
            $noResultsMessage.textContent = 'Something went wrong. Please try again.';
            showState('no-results');
        });
    }

    $loadMore.addEventListener('click', () => {
        const currentML = ml;
        if (!currentML) {
            return;
        }
        $loadMore.disabled = true;
        $loadMoreSpinner.classList.remove('d-none');
        void currentML.load().catch(() => { /* MultiLoader already surfaced the error */ }).finally(() => {
            $loadMore.disabled = false;
            $loadMoreSpinner.classList.add('d-none');
            if (ml === currentML) {
                $loadMore.classList.toggle('d-none', lastPage);
            }
        });
    });

    $clearSearch.addEventListener('click', e => {
        e.preventDefault();
        ml = undefined;
        $input.value = '';
        $resultsList.innerHTML = '';
        $loadMore.classList.add('d-none');
        QueryString().delete('q');
        showState('empty');
    });

    $form.addEventListener('submit', e => {
        e.preventDefault();
        const q = $input.value.trim();
        if (!q) {
            return;
        }
        search(q);
    });

    // Auto-search from query string, or prefill from session
    const initialQ = QueryString().get('q');
    if (initialQ) {
        $input.value = initialQ;
        search(initialQ);
    } else {
        void Session().then(sess => {
            if (sess.user.emailAddress.email && !$input.value) {
                $input.value = sess.user.emailAddress.email;
            }
        }).catch(() => { /* not logged in */ });
    }

    async function renderQuoteCard(quote: models.Quote): Promise<HTMLElement> {
        const el = template.Clone('#quote-template');
        const $card = el.firstElementChild as HTMLElement;

        // Serial
        const $serial = el.querySelector('.quote-serial') as HTMLElement;
        $serial.textContent = `#${quote.serial ?? ''}`;

        // Date
        const $date = el.querySelector('.quote-date') as HTMLElement;
        if (quote.created?.isValid) {
            $date.textContent = quote.created.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' });
        }

        // Line items table
        const $summary = el.querySelector('.quote-summary') as HTMLElement;
        $summary.appendChild(await lineitems.Table(quote.quoteLineItems));

        // Footer note
        const hasShipping = await lineitems.HasShipping(quote.quoteLineItems);
        const $footerNote = el.querySelector('.quote-footer-note') as HTMLElement;
        $footerNote.textContent = hasShipping ?
            '*Sales tax not included in total' :
            '*Sales tax and shipping will be calculated at checkout';

        // Proofs
        const $noProofs = el.querySelector('.quote-no-proofs') as HTMLElement;
        const $proofsParent = el.querySelector('.quote-proofs-parent') as HTMLElement;

        if (quote.quoteProofs.length) {
            $noProofs.classList.add('d-none');
            $proofsParent.classList.remove('d-none');

            const fancyboxGroup = `quote-${quote.id ?? ''}`;

            for (const qp of quote.quoteProofs) {
                const p = qp.proof;
                if (!p) {
                    continue;
                }

                const urls = GetProofURLs(window.cloudFront, p.file || '');
                const proofEl = template.Clone('#quote-proof-template').firstElementChild as HTMLElement;

                const $link = proofEl.querySelector('a') as HTMLAnchorElement;
                $link.href = urls.main;
                $link.setAttribute('data-fancybox', fancyboxGroup);

                const $img = $link.querySelector('img') as HTMLImageElement;
                $img.src = urls.thumb || urls.main;
                $img.alt = qp.name || `Proof ${qp.number ?? ''}`;

                const $label = proofEl.querySelector('.proof-label') as HTMLElement;
                $label.textContent = `Proof ${qp.number ?? ''}`;

                $proofsParent.appendChild(proofEl);
            }
        }

        // Add to cart
        const $addToCart = el.querySelector('.quote-add-to-cart') as HTMLButtonElement;
        carts.CartToggle({
            $btn: $addToCart,
            type: 'quote',
            id: quote.id ?? '',
            key: quote.key,
            addLabel: 'Add to cart',
            addIcon: 'shopping_cart',
        });

        // Ask for revisions
        const $revisions = el.querySelector('.quote-revisions-link') as HTMLAnchorElement;
        $revisions.href = `${Relref('/revisions')}?id=${quote.key || quote.id || ''}`;

        return $card;
    }
}
